import React, { useState, useRef, useCallback } from 'react';
import { Button } from './button';
import { Upload, X, Image, AlertCircle, Crop, Check } from 'lucide-react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const ImageUpload = ({ 
  currentImage, 
  onImageChange, 
  onImageRemove,
  label = "Upload Image",
  description = "Click to upload or drag and drop",
  maxSize = 5, // MB
  aspectRatio = null, // e.g., 16/9, 1 for square, null for free crop
  className = ""
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [showCropper, setShowCropper] = useState(false);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [imgSrc, setImgSrc] = useState('');
  const fileInputRef = useRef(null);
  const imgRef = useRef();
  const canvasRef = useRef();

  const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
  const token = localStorage.getItem('admin_token');

  const handleFileSelect = (file) => {
    setUploadError('');
    
    // Validate file size
    if (file.size > maxSize * 1024 * 1024) {
      setUploadError(`File size must be less than ${maxSize}MB.`);
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file.');
      return;
    }

    // Show cropper with the selected image
    const reader = new FileReader();
    reader.onload = (e) => {
      setImgSrc(e.target.result);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  // Initialize crop when image loads
  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    
    let newCrop;
    if (aspectRatio) {
      newCrop = makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        aspectRatio,
        width,
        height,
      );
    } else {
      newCrop = centerCrop(
        {
          unit: '%',
          width: 90,
          height: 90,
        },
        width,
        height,
      );
    }

    setCrop(newCrop);
  }, [aspectRatio]);

  // Generate cropped image and upload
  const handleCropComplete = useCallback(async () => {
    if (!completedCrop || !imgRef.current || !canvasRef.current) {
      return;
    }

    const image = imgRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
    );

    // Convert canvas to blob and upload
    canvas.toBlob(async (blob) => {
      if (!blob) {
        setUploadError('Failed to create cropped image');
        return;
      }

      // Create a new File object from the blob
      const fileName = `cropped-image-${Date.now()}.png`;
      const croppedFile = new File([blob], fileName, { type: 'image/png' });
      
      await uploadFile(croppedFile);
      setShowCropper(false);
      setImgSrc('');
    }, 'image/png', 0.9);
  }, [completedCrop]);

  const uploadFile = async (file) => {
    setIsUploading(true);
    setUploadError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_BASE}/admin/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();
      
      if (response.ok) {
        onImageChange(result.url);
      } else {
        setUploadError(result.detail || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemove = () => {
    if (onImageRemove) {
      onImageRemove();
    }
    setUploadError('');
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      
      {/* Current Image Preview */}
      {currentImage && (
        <div className="relative inline-block">
          <img 
            src={currentImage} 
            alt="Current image" 
            className="w-32 h-32 object-cover rounded-lg border border-gray-200 shadow-sm"
          />
          <button
            onClick={handleRemove}
            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Upload Area */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all duration-200
          ${isDragging 
            ? 'border-blue-400 bg-blue-50' 
            : 'border-gray-300 hover:border-gray-400'
          }
          ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
          disabled={isUploading}
        />

        {isUploading ? (
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
            <p className="text-sm text-gray-600">Uploading...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
              <Upload className="text-gray-400" size={24} />
            </div>
            <p className="text-sm font-medium text-gray-700 mb-1">{description}</p>
            <p className="text-xs text-gray-500">PNG, JPG, GIF up to {maxSize}MB</p>
          </div>
        )}
      </div>

      {/* Error Message */}
      {uploadError && (
        <div className="flex items-center text-red-600 text-sm">
          <AlertCircle size={16} className="mr-2" />
          {uploadError}
        </div>
      )}

      {/* Upload Button Alternative */}
      <Button
        type="button"
        onClick={handleClick}
        disabled={isUploading}
        className="btn-base btn-secondary btn-sm"
      >
        <Image size={16} className="mr-2" />
        {currentImage ? 'Change Image' : 'Select Image'}
      </Button>

      {/* Image Cropper Modal */}
      {showCropper && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-4xl max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Crop Image</h3>
              <button
                onClick={() => {
                  setShowCropper(false);
                  setImgSrc('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="mb-4">
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={aspectRatio}
              >
                <img
                  ref={imgRef}
                  alt="Crop me"
                  src={imgSrc}
                  style={{ transform: `scale(1) rotate(0deg)` }}
                  onLoad={onImageLoad}
                />
              </ReactCrop>
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                onClick={() => {
                  setShowCropper(false);
                  setImgSrc('');
                }}
                className="btn-base btn-secondary"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleCropComplete}
                disabled={!completedCrop?.width || !completedCrop?.height}
                className="btn-base btn-primary"
              >
                <Crop size={16} className="mr-2" />
                Crop & Upload
              </Button>
            </div>
            
            {/* Hidden canvas for cropping */}
            <canvas
              ref={canvasRef}
              style={{
                display: 'none',
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;